/**
 * rAF-batched, rate-capped publisher. Cursor moves fire far faster than we want
 * to broadcast (raw pointermove can exceed 100Hz); this coalesces a burst into
 * the latest value and emits at most once per `minIntervalMs` (default 33ms ≈
 * 30Hz), aligned to animation frames. Pure of the DOM via injected schedulers so
 * it's unit-testable.
 */
export interface RafThrottleOpts {
  raf?: (cb: (t: number) => void) => number;
  caf?: (h: number) => void;
  now?: () => number;
  minIntervalMs?: number;
}

export interface RafThrottle<T> {
  /** Record the latest value; schedules a publish if none is pending. */
  push: (v: T) => void;
  /** Cancel any pending publish. */
  cancel: () => void;
}

export function createRafThrottle<T>(publish: (v: T) => void, opts: RafThrottleOpts = {}): RafThrottle<T> {
  const raf = opts.raf ?? ((cb) => requestAnimationFrame(cb));
  const caf = opts.caf ?? ((h) => cancelAnimationFrame(h));
  const now = opts.now ?? (() => performance.now());
  const minInterval = opts.minIntervalMs ?? 33;

  let pending = false;
  let handle = 0;
  let last = -Infinity;
  let latest: T;
  let has = false;

  const tick = (): void => {
    pending = false;
    const t = now();
    if (t - last >= minInterval) {
      last = t;
      if (has) { has = false; publish(latest); }
    } else {
      // too soon — re-arm for a later frame so we still emit the latest value
      schedule();
    }
  };

  function schedule(): void {
    if (pending) return;
    pending = true;
    handle = raf(tick);
  }

  return {
    push: (v: T) => { latest = v; has = true; schedule(); },
    cancel: () => { if (pending) { caf(handle); pending = false; } has = false; },
  };
}

export interface RafBatch {
  /** Request `fn` to run on the next frame; repeat calls before that frame
   *  coalesce into a single run (so a burst of remote awareness updates becomes
   *  one render). */
  schedule: () => void;
  cancel: () => void;
}

/** Coalesce many triggers into one `fn` call per animation frame. */
export function createRafBatch(fn: () => void, opts: Pick<RafThrottleOpts, 'raf' | 'caf'> = {}): RafBatch {
  const raf = opts.raf ?? ((cb) => requestAnimationFrame(cb));
  const caf = opts.caf ?? ((h) => cancelAnimationFrame(h));
  let pending = false;
  let handle = 0;
  return {
    schedule: () => {
      if (pending) return;
      pending = true;
      handle = raf(() => { pending = false; fn(); });
    },
    cancel: () => { if (pending) { caf(handle); pending = false; } },
  };
}
